import { create } from 'zustand';
import { useTracking } from '../services/runtime';

/** A newer app version is ready; the runner decides when to reload (never in the middle of a run). */
export const useAppUpdate = create<{ available: boolean; dismissed: boolean }>(() => ({ available: false, dismissed: false }));
export const newVersionReady = () => useAppUpdate.setState({ available: true });
export const dismissUpdate = () => useAppUpdate.setState({ dismissed: true });

/** Activates the waiting service worker and reloads (from vite-plugin-pwa's registerSW). */
let activateWaiting: ((reload?: boolean) => Promise<void>) | null = null;
export const setWorkerUpdater = (fn: (reload?: boolean) => Promise<void>) => {
  activateWaiting = fn;
};

const CHECK_EVERY_MS = 30 * 60_000;
const MIN_GAP_MS = 60_000;
const STARTUP_CHECK_MS = 5_000;
let lastCheck = 0;

const running = () => {
  const s = useTracking.getState().state;
  return s === 'RUNNING' || s === 'PAUSED' || s === 'RESUMING';
};

async function registration() {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

/** Resolves once a freshly found service worker has finished installing and activating (or after `ms`). */
function settled(reg: ServiceWorkerRegistration, ms: number) {
  const sw = reg.installing ?? reg.waiting;
  if (!sw) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const done = () => resolve();
    const t = setTimeout(done, ms);
    sw.addEventListener('statechange', () => {
      if (sw.state === 'activated' || sw.state === 'redundant') {
        clearTimeout(t);
        done();
      }
    });
  });
}

/** Ask the server for a newer app version; when there is one, the update card offers it. */
export async function checkForUpdate() {
  lastCheck = Date.now();
  const reg = await registration();
  await reg?.update().catch(() => undefined);
  // iOS can keep serving the old app without ever seeing the new worker: compare builds directly.
  if (await serverHasNewerBuild()) newVersionReady();
}

/** "Update" on the card: switch to the waiting version, or reload from the network. */
export async function applyUpdate() {
  const reg = await registration();
  if (reg?.waiting && activateWaiting) {
    await activateWaiting(true);
    // The worker reloads the page once it takes over; if it doesn't, force it.
    setTimeout(() => void hardRefresh(), 4000);
    return;
  }
  await hardRefresh();
}

const BUNDLE = /\/assets\/index-[\w-]+\.js/;

/** Whether the server now serves a different app build than the one running. */
async function serverHasNewerBuild() {
  try {
    // A query string the service worker's precache doesn't know, so this reaches the server.
    const res = await fetch(`/?build=${Date.now()}`, { cache: 'no-store' });
    const latest = res.ok ? (await res.text()).match(BUNDLE)?.[0] : undefined;
    const current = Array.from(document.scripts, (s) => s.src).find((src) => BUNDLE.test(src));
    return !!latest && !!current && !current.endsWith(latest);
  } catch {
    return false;
  }
}

/** Pull-to-refresh: pick up a new deploy if there is one, then reload with fresh data. */
export async function hardRefresh() {
  const reg = await registration();
  if (reg) {
    await Promise.race([reg.update().catch(() => undefined), new Promise((r) => setTimeout(r, 5000))]);
    // A new version waits for the runner's go-ahead: this reload is it.
    reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
    await settled(reg, 8000);
    // iOS sometimes keeps serving the old app from the worker's cache even though a new one is
    // deployed: drop the worker and its caches so this reload comes from the network.
    if (await serverHasNewerBuild()) {
      await Promise.all([
        ...(await navigator.serviceWorker.getRegistrations()).map((r) => r.unregister()),
        ...('caches' in window ? (await caches.keys()).filter((k) => k.startsWith('workbox-precache')).map((k) => caches.delete(k)) : []),
      ]).catch(() => undefined);
    }
  }
  window.location.reload();
}

/**
 * An installed app (iOS especially) resumes without navigating, so the browser never looks for
 * a new version. Check shortly after start, when it comes back on screen and every 30 min —
 * never in the middle of a run.
 */
export function watchForUpdates() {
  const maybeCheck = () => {
    if (document.visibilityState !== 'visible' || running() || Date.now() - lastCheck < MIN_GAP_MS) return;
    void checkForUpdate();
  };
  document.addEventListener('visibilitychange', () => {
    // "Later" lasts until the app is opened again.
    if (document.visibilityState === 'visible') useAppUpdate.setState({ dismissed: false });
    maybeCheck();
  });
  setInterval(maybeCheck, CHECK_EVERY_MS);
  setTimeout(maybeCheck, STARTUP_CHECK_MS);
}
